import { useState, useEffect } from 'react';
import ImageUpload from '../ImageUpload';

interface Product {
  id?: number;
  name: string;
  weight: string;
  image: string;
  display_order: number;
}

export default function ProductsSection() {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch('https://mulata.wortik.com/bolacha-mulata/api/products.php');
      const result = await response.json();
      setProducts(result);
    } catch (err) {
      setMessage('Erro ao carregar produtos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveProduct = async (product: Product) => {
    setIsSaving(true);
    setMessage('');

    try {
      const url = 'https://mulata.wortik.com/bolacha-mulata/api/products.php';
      const method = product.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(product)
      });

      const result = await response.json();
      
      if (result.success) {
        setMessage('Produto salvo com sucesso!');
        fetchProducts();
        setEditingProduct(null);
        setIsAdding(false);
      } else {
        setMessage('Erro ao salvar produto');
      }
    } catch (err) {
      setMessage('Erro ao salvar produto');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteProduct = async (id: number) => {
    if (!confirm('Tem certeza que deseja excluir este produto?')) return;

    try {
      const response = await fetch(`https://mulata.wortik.com/bolacha-mulata/api/products.php?id=${id}`, {
        method: 'DELETE'
      });

      const result = await response.json();
      
      if (result.success) {
        setMessage('Produto excluído com sucesso!');
        fetchProducts();
      } else {
        setMessage('Erro ao excluir produto');
      }
    } catch (err) {
      setMessage('Erro ao excluir produto');
    }
  };

  const ProductForm = ({ product, onSave, onCancel }: { product: Product; onSave: (p: Product) => void; onCancel: () => void }) => {
    const [formData, setFormData] = useState(product);

    return (
      <div className="bg-gray-50 p-4 rounded-lg space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Produto</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            placeholder="Pack Mulata"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Peso</label>
          <input
            type="text"
            value={formData.weight}
            onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            placeholder="3x175g"
          />
        </div>
        
        <ImageUpload
          currentImage={formData.image}
          onImageChange={(path) => setFormData({ ...formData, image: path })}
          label="Imagem do Produto"
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Ordem de Exibição</label>
          <input
            type="number"
            value={formData.display_order}
            onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            placeholder="1"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onSave(formData)}
            disabled={isSaving}
            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200 disabled:opacity-50"
          >
            {isSaving ? 'Salvando...' : 'Salvar'}
          </button>
          <button
            onClick={onCancel}
            className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg transition duration-200"
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  };

  if (isLoading) return <div>Carregando...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Produtos</h2>
        <button
          onClick={() => setIsAdding(true)}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition duration-200"
        >
          + Adicionar Produto
        </button>
      </div>

      {isAdding && (
        <ProductForm
          product={{ name: '', weight: '', image: '', display_order: products.length + 1 }}
          onSave={handleSaveProduct}
          onCancel={() => setIsAdding(false)}
        />
      )}

      <div className="space-y-4">
        {products.map((product) => (
          <div key={product.id} className="border border-gray-200 rounded-lg p-4">
            {editingProduct && editingProduct.id === product.id ? (
              <ProductForm
                product={editingProduct}
                onSave={handleSaveProduct}
                onCancel={() => setEditingProduct(null)}
              />
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <img src={product.image} alt={product.name} className="w-16 h-16 object-contain" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{product.name}</h3>
                    <p className="text-sm text-gray-600">{product.weight}</p>
                    <p className="text-xs text-gray-500">Ordem: {product.display_order}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingProduct(product)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded transition duration-200"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleDeleteProduct(product.id!)}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded transition duration-200"
                  >
                    Excluir
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {message && (
        <div className={`p-4 rounded-lg ${message.includes('sucesso') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}
    </div>
  );
}
